/**
 * Flow editor — draft autosave.
 *
 * Keeps a copy of the in-progress chart in localStorage so a page refresh
 * doesn't lose work. Writes are debounced so we don't thrash on every
 * click; reads happen once at editor mount.
 *
 * What's persisted and where:
 *   localStorage  — chart JSON, song metadata (name/author), active diff,
 *                   audio filename, duration, playhead position
 *   IndexedDB     — raw audio bytes (too big for localStorage), cover image
 *                   bytes (also binary / potentially large)
 *
 * On restore, the chart + metadata land synchronously; the audio and cover
 * blobs re-attach asynchronously once IndexedDB replies.
 */

import { useFlowStore } from './flowStore';
import {
  loadCachedAudio,
  clearCachedAudio,
  cacheCoverImage,
  loadCachedCover,
  clearCachedCover,
} from './audioCache';
import type { FlowChart } from './types';

const STORAGE_KEY = 'synapse-flow-draft';
const SAVE_DEBOUNCE_MS = 600;

interface DraftSnapshot {
  format: 'synapse-flow-draft';
  version: number;      // 1 = legacy (no song meta), 2 = adds song_name / song_author / cover_name
  saved_at: string;
  chart: FlowChart;
  activeDifficulty: string;
  song_name: string;
  song_author: string;
  audio_name: string | null;
  duration_seconds: number;
  /** Wallclock position so refresh doesn't reset the playhead. */
  current_time: number;
  /** Original filename of the cover (used only to detect changes in
   *  snapshotKey; the actual bytes live in IndexedDB). */
  cover_name: string | null;
}

let _saveTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * True when the store is at its pristine boot state: no audio, no song meta,
 * and zero notes in every difficulty. We must NEVER persist this — otherwise a
 * transient default state (e.g. during boot, before restore applies) could
 * clobber a perfectly good draft and the user would lose their map on refresh.
 */
function isPristineDefault(s: ReturnType<typeof useFlowStore.getState>): boolean {
  const dn = s.chart.difficultyNotes ?? {};
  const anyDiffNotes = (['Easy', 'Medium', 'Hard', 'Expert'] as const)
    .some(k => (dn[k]?.length ?? 0) > 0);
  return !s.audioName
    && !s.songName?.trim()
    && !s.songAuthor?.trim()
    && (s.chart.notes?.length ?? 0) === 0
    && !anyDiffNotes;
}

function writeDraftNow(): void {
  // A pending debounced write has now been flushed — drop the timer handle.
  if (_saveTimer) { clearTimeout(_saveTimer); _saveTimer = null; }
  try {
    const s = useFlowStore.getState();
    // Guard: don't overwrite a real draft with the empty default state.
    if (isPristineDefault(s)) return;
    const snapshot: DraftSnapshot = {
      format: 'synapse-flow-draft',
      version: 2,
      saved_at: new Date().toISOString(),
      chart: s.chart,
      activeDifficulty: s.activeDifficulty,
      song_name: s.songName,
      song_author: s.songAuthor,
      audio_name: s.audioName,
      duration_seconds: s.duration,
      current_time: s.currentTime,
      cover_name: s.coverName,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));

    // Cover bytes → IndexedDB (fire-and-forget, non-fatal on failure).
    if (s.coverBytes && s.coverName && s.coverMime) {
      cacheCoverImage(s.coverBytes, s.coverName, s.coverMime).catch(() => {});
    }
  } catch { /* quota / blocked storage — ignore */ }
}

function scheduleSave(): void {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(writeDraftNow, SAVE_DEBOUNCE_MS);
}

/**
 * Subscribe to chart-affecting store changes and save with a debounce.
 * Returns the unsubscribe function so the caller can detach on unmount.
 */
export function attachDraftAutosave(): () => void {
  let prev = snapshotKey(useFlowStore.getState());

  const unsub = useFlowStore.subscribe(state => {
    const key = snapshotKey(state);
    if (key !== prev) {
      prev = key;
      scheduleSave();
    }
  });

  // Flush synchronously when the page is being hidden or unloaded (refresh /
  // tab close / navigation). localStorage writes are synchronous, so this
  // commits the latest chart even if the 600ms debounce hadn't fired yet.
  // ALWAYS write (not just when a save is pending) so the live playhead
  // position (currentTime) is captured too — it's excluded from snapshotKey
  // (to avoid thrashing on every playback tick), so scrubbing/playing without
  // an edit wouldn't otherwise schedule a save and refresh would jump elsewhere.
  const flushOnExit = () => { writeDraftNow(); };
  window.addEventListener('pagehide', flushOnExit);
  window.addEventListener('beforeunload', flushOnExit);
  // visibilitychange covers mobile/background-tab cases pagehide can miss.
  const onVisibility = () => { if (document.visibilityState === 'hidden') flushOnExit(); };
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    // On unmount (SPA navigation away), flush so the latest edits AND the
    // current playhead position are captured (not just a pending debounce).
    writeDraftNow();
    unsub();
    window.removeEventListener('pagehide', flushOnExit);
    window.removeEventListener('beforeunload', flushOnExit);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

function snapshotKey(s: ReturnType<typeof useFlowStore.getState>): string {
  // Cheap signature that changes when any persisted field changes.
  // currentTime is excluded so playback ticking doesn't fire saves.
  const fp = (notes: typeof s.chart.notes) =>
    `${notes.length}:${notes.map(n => `${n.id}|${n.lane}|${n.time_beats}|${n.type}|${n.duration_beats}`).join(',')}`;

  // Include every inactive difficulty's note set so switching diffs flushes.
  const dn = s.chart.difficultyNotes ?? {};
  const dnKey = (['Easy', 'Medium', 'Hard', 'Expert'] as const)
    .map(k => `${k}=${fp(dn[k] ?? [])}`)
    .join('|');

  return JSON.stringify([
    s.chart.bpm,
    s.chart.offset_seconds,
    fp(s.chart.notes),
    dnKey,
    s.activeDifficulty,
    s.audioName,
    s.songName,
    s.songAuthor,
    s.coverName,   // cover bytes in IndexedDB; name change = new cover
  ]);
}

/**
 * Load the draft on editor boot. If a draft exists and looks valid, apply it
 * to the store. Returns true if a draft was restored.
 *
 * The audio and cover cache lookups are async (IndexedDB), so we kick them
 * off but don't block the synchronous part of the restore — chart + metadata
 * land immediately; the blobs re-attach a moment later.
 */
export function restoreDraftIfAny(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<DraftSnapshot>;
    if (parsed.format !== 'synapse-flow-draft') return false;
    // Accept both version 1 (legacy, no song meta) and version 2.
    if (parsed.version !== 1 && parsed.version !== 2) return false;
    if (!parsed.chart || typeof parsed.chart !== 'object') return false;

    const s = useFlowStore.getState();
    s.setChart(parsed.chart as FlowChart);
    // The saved chart already has its notes arranged for this active diff,
    // so use the non-swapping setter to avoid shuffling them into the wrong slot.
    s.setActiveDifficultyAfterLoad(parsed.activeDifficulty || 'Hard');
    if (parsed.duration_seconds && parsed.duration_seconds > 0) {
      s.setDuration(parsed.duration_seconds);
    }
    if (parsed.current_time && parsed.current_time > 0) {
      s.setCurrentTime(parsed.current_time);
    }
    // Restore song metadata (version-2 only; version-1 drafts skip silently).
    if (parsed.song_name) s.setSongName(parsed.song_name);
    if (parsed.song_author) s.setSongAuthor(parsed.song_author);

    // ── Async: reattach audio from IndexedDB ──────────────────────────────
    void (async () => {
      const cached = await loadCachedAudio();
      if (!cached) return;
      const current = useFlowStore.getState();
      if (current.audioUrl) {
        // Something loaded audio since restore began — don't overwrite.
        try { URL.revokeObjectURL(cached.url); } catch { /* ignore */ }
        return;
      }
      // Only auto-restore audio if the filename matches the draft's expectation.
      if (parsed.audio_name && parsed.audio_name !== cached.name) {
        try { URL.revokeObjectURL(cached.url); } catch { /* ignore */ }
        return;
      }
      current.setAudio({
        url: cached.url,
        name: cached.name,
        bpm: (parsed.chart as FlowChart).bpm,
        offsetSeconds: (parsed.chart as FlowChart).offset_seconds,
        durationSeconds: parsed.duration_seconds ?? 0,
      });
      if (parsed.current_time && parsed.current_time > 0) {
        useFlowStore.getState().setCurrentTime(parsed.current_time);
      }
    })();

    // ── Async: reattach cover from IndexedDB ──────────────────────────────
    void (async () => {
      const cachedCover = await loadCachedCover();
      if (!cachedCover) return;
      const current = useFlowStore.getState();
      // Don't overwrite a cover already set (e.g. from a chart that loaded first).
      if (current.coverUrl) return;
      // Only restore cover if the name matches what the draft recorded.
      if (parsed.cover_name && parsed.cover_name !== cachedCover.name) return;
      current.setCover(cachedCover);
    })();

    return true;
  } catch {
    return false;
  }
}

/**
 * Erase the draft AND all cached binary assets.
 * Called on explicit Clear and (optionally) on successful server-save.
 */
export function clearDraft(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  void clearCachedAudio();
  void clearCachedCover();
}
